import React from "react";
import Modal from "@components/common/Modal/Modal";
import AddEquipmentForm from "./AddEquipmentForm";
import type { CreateEquipmentRequest, Equipment } from "../types/types";
import AddIcon from "@mui/icons-material/Add";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import { EditActionIcon } from "@components/common/Icons/ActionIcons";
import { useEquipmentModal } from "../hooks/useEquipmentModal";
import { useSelector } from "react-redux";
import type { RootState } from "@store/store";
import { useTranslation } from "react-i18next";

interface AddEquipmentModalProps {
	open: boolean;
	onClose: () => void;
	initialValues?: Equipment | null;
	isEdit?: boolean;
	isView?: boolean;
}

const AddEquipmentModal: React.FC<AddEquipmentModalProps> = ({
	open,
	onClose,
	initialValues,
	isEdit = false,
	isView = false,
}) => {
	const { t } = useTranslation();
	const { handleCreateEquipment, handleUpdateEquipment, isCreatingEquipment, isUpdatingEquipment } =
		useEquipmentModal();
	const modalState = useSelector((state: RootState) => state?.modal);

	const handleSubmit = async (data: CreateEquipmentRequest) => {
		if (isView) return;
		if (isEdit && modalState?.editEquipmentModal?.equipment) {
			await handleUpdateEquipment(data, modalState?.editEquipmentModal?.equipment?.uuid);
		} else if (isEdit && initialValues?.uuid) {
			await handleUpdateEquipment(data, initialValues.uuid);
		} else {
			await handleCreateEquipment(data);
		}
	};

	const isSubmitting = isView ? false : isEdit ? isUpdatingEquipment : isCreatingEquipment;
	const title = isView
		? t("feature.courseBuilder.equipments.modals.viewEquipment")
		: isEdit
			? t("feature.courseBuilder.equipments.modals.editEquipment")
			: t("feature.courseBuilder.equipments.modals.addEquipment");
	const icon = isView ? <VisibilityOutlinedIcon /> : isEdit ? <EditActionIcon /> : <AddIcon />;

	return (
		<Modal isOpen={open} onClose={onClose} title={title} icon={icon} width="max-w-2xl" showCloseIcon={!isSubmitting}>
			<AddEquipmentForm
				onSubmit={handleSubmit}
				onCancel={onClose}
				isSubmitting={isSubmitting}
				initialValues={initialValues}
				isEdit={isEdit}
				isView={isView}
			/>
		</Modal>
	);
};

export default AddEquipmentModal;
